import { Button, H2, H5 } from "@sellar-io/components";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useIntercom } from "react-use-intercom";
import { Alert } from "../../../../../components/alert/Alert";
import { ProfilePicture, SpinnerLegacy } from "../../../../../components";
import { useSellarUser } from "../../../../../hooks";
import { useBusinessClaim } from "../../../../../hooks/api/v2/business/useBusinessClaim";
import { useSellarDistributionContactCreate } from "../../../../../hooks/api/v2/sellarDistributionContact/useSellarDistributionContactCreate";
import { useOrderLinkFlowStorefront } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowStorefront";
import { useUser } from "../../../../../hooks/api/v2/user/useUser";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../hooks/useSignupContext.shared";
import {
  BusinessClaimConfirmation,
  TermsAndConditionsConfirmation,
} from "./BuyerSignupFormStep";

export const BuyerBusinessConnectStep = () => {
  const [searchParams] = useSearchParams();
  const orderFlowToken = searchParams.get("orderFlowToken");
  const {
    setStep,
    hasAcceptedTermsAndConditions,
    setHasAcceptedTermsAndConditions,
  } = useSignupContext();
  const { user: sellarUser, changeBusiness } = useSellarUser();
  const sellarUserId = sellarUser?.id ?? 0;
  const { data: userInDb } = useUser(sellarUserId, {
    enabled: Boolean(sellarUserId),
  });
  const { show } = useIntercom();
  const claimBusinessMutation = useBusinessClaim();
  const [error, setError] = useState<string | null>(null);
  const [confirmedAssociation, setConfirmedAssociation] = useState(false);

  const { data, isLoading } = useOrderLinkFlowStorefront(orderFlowToken, {
    enabled: !!orderFlowToken,
  });

  const retailerId = data?.storefront.retailerId;
  const sellarDistributionContactCreate = useSellarDistributionContactCreate(
    retailerId ? { retailerId } : undefined,
  );
  const buyerBusinessId = data?.storefront.retailerBusinessId;
  const buyerBusinessName = data?.storefront.retailerBusinessName;
  const retailerBusinessLogoUrl = data?.storefront.retailerBusinessLogoUrl;

  const handleClaimBusiness = async () => {
    if (!buyerBusinessId) return;

    try {
      const claimBusinessResult = await claimBusinessMutation.mutateAsync({
        data: {
          id: buyerBusinessId,
          hasAcceptedTermsAndConditions,
        },
      });

      changeBusiness(buyerBusinessId);
      if (sellarUser?.email && userInDb?.smsNumber && userInDb.name) {
        await sellarDistributionContactCreate.mutateAsync({
          name: userInDb.name,
          phoneNumber: userInDb.smsNumber,
          email: sellarUser.email,
          BusinessUserId: claimBusinessResult.businessUserId,
        });
      }

      setStep(SignupFlowStep.ConfirmationStep);
    } catch {
      setError(
        "Unable to claim this business. It may already be claimed or there was an error.",
      );
    }
  };

  const handleContactSupport = () => {
    show();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <SpinnerLegacy />
      </div>
    );
  }

  if (claimBusinessMutation.isPending) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <SpinnerLegacy />
        <H5 className="text-gray-600">Claiming your business...</H5>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-6 p-8">
      <div className="flex flex-col gap-2">
        <H2 className="text-center">Is this your business?</H2>
        <p className="text-center text-gray-600">
          Please confirm this is the business you are associated with
        </p>
      </div>

      {error && <Alert type="error" className="w-full" message={error} />}

      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-3 rounded-lg border border-solid p-4">
          <ProfilePicture
            name={buyerBusinessName ?? ""}
            logoUrl={retailerBusinessLogoUrl ?? ""}
            className="h-12 w-12"
          />
          <span className="font-semibold">{buyerBusinessName}</span>
        </div>
      </div>

      <div className="col-span-2 mt-auto flex flex-col gap-3">
        <BusinessClaimConfirmation
          onChange={(value) => setConfirmedAssociation(value)}
        />
        <TermsAndConditionsConfirmation
          onChange={(value) => setHasAcceptedTermsAndConditions(value)}
        />
        <Button
          onClick={handleClaimBusiness}
          disabled={!confirmedAssociation || !hasAcceptedTermsAndConditions}
          className="w-full"
        >
          Yes, claim this business
        </Button>
        <Button
          onClick={handleContactSupport}
          color="secondary"
          className="w-full"
        >
          Contact support
        </Button>
        <p className="text-center text-sm text-gray-500">
          Don&apos;t recognise your business?
        </p>
      </div>
    </div>
  );
};
